import meiliSearchApiClient from 'src/api/clients/meilisearch';
import tenantApi from 'src/api/clients/tenant';
import { SearchState } from 'src/hooks/meilisearch/types/search-state';
import { MeiliSearchResponse } from 'src/types/responses/meilisearch';
import { calculatePaginationOffset } from 'src/utils/calculate-pagination-offset';
import { AssignBookingRulesToEmployeeGroupsPayload } from '../types/payload/assign-booking-rules-to-employee-groups.payload';
import { CreateBookingRulesPayload } from '../types/payload/create-booking-rules.payload';
import { EditBookingRulesDurationTypePayload } from '../types/payload/edit-booking-rules-duration-type.payload';
import { EditBookingRulesSettingsPayload } from '../types/payload/edit-booking-rules-settings.payload';
import { UnassignBookingRulesFromEmployeeGroupPayload } from '../types/payload/unassign-booking-rules-from-employee-group.payload';
import { BookingRulesResponse } from '../types/response/booking-rule.response';

export const fetchBookingRules = async (
  tenantToken: string,
  searchState: SearchState,
  page: number,
  perPage: number,
) => {
  const { data } = await meiliSearchApiClient.post<
    MeiliSearchResponse<BookingRulesResponse>
  >(`/indexes/BookingRules-${tenantToken}/search`, {
    ...searchState,
    limit: perPage,
    offset: calculatePaginationOffset(page, perPage),
  });

  return data;
};

export const createBookingRules = async (
  payload: CreateBookingRulesPayload,
): Promise<string> => {
  const { data } = await tenantApi.post<{ token: string }>(
    '/booking-rules',
    payload,
  );

  return data.token;
};

export const assignBookingRulesToEmployeeGroups = async (
  payload: AssignBookingRulesToEmployeeGroupsPayload,
): Promise<void> => {
  await tenantApi.post<void>(
    `/booking-rules/${payload.bookingRulesToken}/employee-groups`,
    {
      employeeGroups: payload.employeeGroups,
    },
  );
};

export const unassignBookingRulesFromEmployeeGroup = async ({
  bookingRulesToken,
  employeeGroupToken,
  spaceType,
  bookingsDecisions,
  bookingsDecisionsReason,
}: UnassignBookingRulesFromEmployeeGroupPayload): Promise<void> => {
  await tenantApi.post<void>(
    `/booking-rules/${bookingRulesToken}/employee-groups/${employeeGroupToken}/unassign`,
    {
      spaceType,
      bookingsDecisions,
      bookingsDecisionsReason,
    },
  );
};

export const editBookingRulesName = async (
  token: string,
  name: string,
): Promise<void> => {
  await tenantApi.post<void>(`/booking-rules/${token}/name`, { name });
};

export const editBookingRulesSettings = async ({
  token,
  rules,
  bookingsDecisions,
  bookingsDecisionsReason,
}: EditBookingRulesSettingsPayload): Promise<void> => {
  await tenantApi.put<void>(`/booking-rules/${token}/rules`, {
    rules,
    bookingsDecisions,
    bookingsDecisionsReason,
  });
};

export const editBookingRulesDurationType = async ({
  token,
  durationType,
  bookingsDecisions,
  bookingsDecisionsReason,
}: EditBookingRulesDurationTypePayload): Promise<void> => {
  await tenantApi.post<void>(`/booking-rules/${token}/duration`, {
    durationType,
    bookingsDecisions,
    bookingsDecisionsReason,
  });
};

export const deleteBookingRules = async (token: string): Promise<void> => {
  await tenantApi.delete<void>(`/booking-rules/${token}`);
};
